import { Component, Input, OnInit } from '@angular/core';
import { UIRouter }                        from '@uirouter/core';
import { TranslateService }               from '@ngx-translate/core';

import { ApiService }              from '../api/api.service';
import { CompanyService }          from '../companies/company.service';
import { ConfirmService }          from '../messages/confirm/confirm.service';
import { CustomerFactoryService }  from './customer-factory.service';
import { CustomerIntegrationsService } from './customer-integrations.service';
import { MessagesService }        from '../messages/messages.service';
import { SessionService }         from '../sessions/session.service';
import { StreamSearchService }    from '../api/stream-search.service';

import { CompanyCountryValidationsModel } from '../companies/models/company.country-validations';
import { CompanyMessagesModel }           from '../companies/models/company.messages';
import { CompanyValidationModel }         from '../companies/models/company.validation';
import { CustomerModel }                  from './models/customer';
import { InvoiceModel }                   from '../invoices/models/invoice';
import { SelectOption }                   from '../core/models/select-option';
import { SessionModel }                   from '../sessions/models/session';
import { TaxGroupModel }                  from '../companies/models/tax-group';
import { VehicleModel }                   from '../vehicles/models/vehicle';

@Component({
  selector   : 'wor-customer',
  template: require('./customer.component.html')
})
export class CustomerComponent implements OnInit {
  @Input() ams_autoclubs        : SelectOption[];
  @Input() billedCustomers     : CustomerModel[];
  @Input() biller_balance      : string;
  @Input() biller_credit_balance: string;
  @Input() companyMessages     : CompanyMessagesModel;
  @Input() contact_method_types: SelectOption[];
  @Input() customer            : CustomerModel;
  @Input() customerSources     : SelectOption[];
  @Input() events              : Record<string, unknown>[];
  @Input() inspections         : Record<string, unknown>[];
  @Input() invoices            : InvoiceModel[];
  @Input() payments            : Record<string, unknown>[];
  @Input() paymentTerms        : SelectOption[];
  @Input() price_types         : SelectOption[];
  @Input() quotes              : Record<string, unknown>[];
  @Input() states              : SelectOption[];
  @Input() statusTypes         : SelectOption[];
  @Input() taxGroups           : TaxGroupModel[];
  @Input() vehicles            : VehicleModel[];

  addressSearch      : any;
  biller             : { balance: string; credit_balance: string };
  billerSearch       : any;
  countryTypes       : SelectOption[];
  customerObj        : CustomerFactoryService;
  loaded             = true;
  loading            = false;
  showCustomerDetails = true;
  showContactDetails  = false;
  validations        : CompanyCountryValidationsModel;

  constructor (
    private apiService              : ApiService,
    public  companyService          : CompanyService,
    private confirmService          : ConfirmService,
    private customerFactoryService  : CustomerFactoryService,
    public  integrationsService     : CustomerIntegrationsService,
    private messagesService         : MessagesService,
    private router                  : UIRouter,
    public  sessionService          : SessionService,
    private streamSearchService     : StreamSearchService,
    private translateService        : TranslateService
  ) {}

  get session () : SessionModel { return this.sessionService.base(); }
  get currency () : any { return this.sessionService.currency(); }

  ngOnInit () {
    if (!this.customer) {
      this.customer = {
        ams_autoclub: this.sessionService.company().ams_rewards_settings?.ams_default_autoclub,
        cash_or_account: 'C',
        customer_limited: false,
        customer_type: 'I',
        default_payment_terms: this.sessionService.company().default_payment_terms,
        price_type: '1'
      };
    }

    this.biller = {
      balance: this.biller_balance,
      credit_balance: this.biller_credit_balance
    };

    this.customerObj = this.customerFactoryService.init(this.customer);

    this.addressSearch = {
      list: [],
      selected: { id: '', full_address: '' },
      choose: (address, type = 'address') => {
        this.loading = true;
        return this.apiService.get('/NzPostal/get_address_details', address.address_id)
        .then(details => {
          if (type === 'postal') {
            this.customer.postal_address1 = details.address1;
            this.customer.postal_address2 = details.address2;
            this.customer.postal_suburb = details.suburb;
            this.customer.postal_state = details.state;
            this.customer.postal_postcode = details.postcode;
          } else {
            this.customer.address1 = details.address1;
            this.customer.address2 = details.address2;
            this.customer.suburb = details.suburb;
            this.customer.state = details.state;
            this.customer.postcode = details.postcode;
            this.customer.country_code = details.country_code;
            this.customer.latitude = details.latitude;
            this.customer.longitude = details.longitude;
          }
        }).finally(() => this.loading = false);
      },
      format: (address) => typeof address === 'string' ? address : '',
      search: (typed) => this.apiService.get('/NzPostal/get_addresses', typed)
        .then(addresses => addresses !== 'null' ? addresses : '')
    };

    this.billerSearch = {
      list: [],
      selected: null as any,
      choose: (customer) => {
        this.customer.biller_id = customer.id || null;
        this.customer.biller_name = customer.display_name || null;
      },
      format: (customer) => customer ? customer.display_name : '',
      search: (typed) => this.streamSearchService.search('customer_billers', typed)
        .then(customers => this.billerSearch.list.concat(customers))
    };

    if (this.customer.price_type === '5' && this.customer.price_matrix_id) {


      this.customer.price_type = this.customer.price_matrix_id;


    }

    if (this.customer.biller_id) {


      this.getBiller(this.customer.biller_id);


    }

    if (this.customer.country_code) {


      this.setCountryDefaults();


    }

    this.apiService.get('/system/country_types/', null, 'country_types').then(types => this.countryTypes = types);

    this.integrationsService.init({
      customer          : this.customer,
      isBranchCustomer  : !!this.customerObj.isBranchCustomer?.()
    });
  }

  archive () {
    const archived = !this.customer.archived;
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.ARCHIVE', {
      archived, name: this.customer.display_name
    }))
    .then(() => this.apiService.post('/customers/toggle_archived', { archived, customer_id: this.customer.id }))
    .then(() => {
      this.messagesService.show(this.translateService.instant('CUSTOMER_SPACE.ARCHIVED_SUCCESSFULLY', { archived }), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  createFromOtherBranch () {
    this.customerFactoryService.createCustomerFromOtherBranch(false)
    .then(results => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.CUSTOMER'), 'success');
      this.router.stateService.go('app.customer', { id: results.customer.id });
    });
  }

  delete () {
    this.customerObj.delete()
    .then(() => {
      this.router.stateService.go('app.customers');
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.CUSTOMER'), 'success');
    }).catch(err => this.messagesService.error(err));
  }

  emailStatement () {
    if (!this.customer.id) {

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MUST_SAVE_CUSTOMER_BEFORE_EMAILING'), 'warning');

      return;

    }
    this.customerObj.emailStatement()
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.EMAIL_SENT'), 'success'));
  }

  getBiller (id: string) {
    this.apiService.get('/customer', id).then(customer => {
      this.billerSearch.selected = { display_name: customer.display_name, id: customer.id };
      this.customer.biller_name = customer.display_name;
    }).catch(err => this.messagesService.error(err));
  }

  print () {
    if (!this.customer.id) {

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MUST_SAVE_CUSTOMER_BEFORE_PRINTING'), 'warning');

      return;

    }
    this.customerObj.printStatement();
  }

  save () {
    if (this.customer.tax_group_id) {

      this.customer.tax_group_name = this.taxGroups?.find(g => g.id === this.customer.tax_group_id)?.name;

    }

    const billerEl = document.getElementsByName('biller')[0] as HTMLInputElement;
    if (billerEl && billerEl.value === '') {

      this.customer.biller_id = null;

    }

    this.customerObj.save().then(customer => {
      this.customer = customer;
      this.customerObj = this.customerFactoryService.init(this.customer);
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.CUSTOMER'), 'success');
      this.router.stateService.go('app.customer', { id: this.customer.id }, { reload: true });
    }).catch(err => this.messagesService.error(err));
  }

  sendEmail (email?: string) {


    this.customerObj.email(email || this.customer.email, this.companyMessages?.customer_email_message)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(err => this.messagesService.error(err));


  }

  sendPortalInvitation () {


    this.customerObj.sendPortalInvitation()
    .then(() => this.router.stateService.reload())
    .catch(err => this.messagesService.error(err));


  }

  sendSms () {


    this.customerObj.sms(this.customer)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(err => this.messagesService.error(err));


  }

  setCountryDefaults () {
    this.apiService.get('/system/states_for_country', this.customer.country_code).then(states => {
      this.states = states;
      return this.apiService.get('/system/country_validations', this.customer.country_code);
    }).then(validations => this.validations = validations)
    .catch(err => this.messagesService.error(err));
  }

  syncWithHeadOffice () {
    Promise.resolve()
    .then(() => this.customerObj.save())
    .then(() => this.apiService.patch('/customers/sync_multibranch_customer_entry', this.customer))
    .then(() => {
      this.messagesService.show(this.translateService.instant('MULTI_BRANCH_SPACE.CUSTOMER_LINKED'), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  checkRights (module: string): boolean {


    return this.sessionService.hasRights(module);


  }
  validateCompanyName (name: string) {

    return this.customerObj.validateCompanyName(name);

  }
  validateFirstAndLastName (name: string, field = 'first_name') {

    return this.customerObj.validateFirstAndLastName(name, field);

    };

  }
